/**
 * Car Creator – DreamMee MVP
 * Same layered-PNG architecture as CharacterCreator.
 * Supports two modes:
 *   1. Standalone: renders into a visible canvas + controls panel (Step 4)
 *   2. Offscreen: renders into a hidden stage for scene snapshot export
 *
 * Per-part gradient tone mapping with black-preservation threshold.
 */

class CarCreator {
  constructor(config, options = {}) {
    this.config = config;

    // Build default tone selections — one per gradient-eligible category, all 'none'
    const defaultTones = {};
    for (const cat of Object.keys(CAR_GRADIENT_TONE_GROUPS)) {
      defaultTones[cat] = 'none';
    }

    // State
    this.state = {
      model: config.defaults.model,
      selections: { ...config.defaults },
      toneSelections: defaultTones
    };

    // Options
    this.canvasContainerId = options.canvasContainerId || null;
    this.controlsPanelId = options.controlsPanelId || null;
    this.isStandalone = !!this.canvasContainerId;

    // Konva
    this.stage = null;
    this.layers = {};
    this.imageCache = new Map();
    this.maxCacheSize = (typeof DREAMMEE_DEPLOY !== 'undefined' && DREAMMEE_DEPLOY.maxImageCacheSize) || 80;
    this.loadCounters = {};

    // Accordion UI state
    this.openAccordion = null;
    this.activeSubSection = {}; // per accordion group — tracks active sub-tab

    if (this.isStandalone) {
      this.initStandalone();
    } else {
      this.initOffscreen();
    }
  }

  // ==================== INITIALIZATION ====================

  initStandalone() {
    const { width, height } = this.config.canvas;

    this.stage = new Konva.Stage({
      container: this.canvasContainerId,
      width,
      height
    });

    this.recreateLayers();
    this.renderControls();
    this.handleResize();
    window.addEventListener('resize', () => this.handleResize());
  }

  initOffscreen() {
    const container = document.createElement('div');
    container.id = 'car-offscreen-' + Date.now();
    container.style.cssText = 'position:absolute;left:-9999px;top:-9999px;';
    container.style.width = this.config.canvas.width + 'px';
    container.style.height = this.config.canvas.height + 'px';
    document.body.appendChild(container);

    this.stage = new Konva.Stage({
      container: container.id,
      width: this.config.canvas.width,
      height: this.config.canvas.height
    });

    this.recreateLayers();
  }

  recreateLayers() {
    Object.values(this.layers).forEach(l => {
      this.deallocateCanvases(l);
      l.destroy();
    });
    this.layers = {};

    const model = this.config.models.find(m => m.id === this.state.model);
    if (!model) return;

    model.zIndexConfig.forEach(layerId => {
      const layer = new Konva.Layer();
      this.layers[layerId] = layer;
      this.stage.add(layer);
    });
  }

  deallocateCanvases(node) {
    if (!node) return;
    node.find('Image').forEach(imgShape => {
      const img = imgShape.image();
      if (img && img instanceof HTMLCanvasElement) {
        img.width = 0;
        img.height = 0;
      }
    });
  }

  destroy() {
    Object.values(this.layers).forEach(l => {
      this.deallocateCanvases(l);
      l.destroy();
    });
    this.layers = {};
    if (this.stage) {
      this.stage.destroy();
      this.stage = null;
    }
    this.imageCache.clear();
    const container = document.getElementById(this.canvasContainerId);
    if (container) {
      container.innerHTML = '';
    }
  }

  handleResize() {
    if (!this.isStandalone) return;
    const container = document.getElementById(this.canvasContainerId);
    if (!container) return;
    const containerWidth = container.offsetWidth;
    if (containerWidth === 0) return;
    const scale = containerWidth / this.config.canvas.width;
    this.stage.width(this.config.canvas.width * scale);
    this.stage.height(this.config.canvas.height * scale);
    this.stage.scale({ x: scale, y: scale });
  }

  // ==================== PRELOAD ====================

  async preloadAllAssets() {
    const urls = new Set();
    for (const modelId of Object.keys(this.config.assets)) {
      const categories = this.config.assets[modelId];
      for (const category of Object.keys(categories)) {
        for (const asset of categories[category]) {
          if (asset.src) urls.add(asset.src);
        }
      }
    }

    const urlArray = Array.from(urls);
    console.log(`[Car] Background preloading ${urlArray.length} assets...`);
    
    const concurrency = 5;
    let loaded = 0;
    
    for (let i = 0; i < urlArray.length; i += concurrency) {
      const chunk = urlArray.slice(i, i + concurrency);
      const results = await Promise.allSettled(chunk.map(src => this.loadImage(src)));
      loaded += results.filter(r => r.status === 'fulfilled').length;
      await new Promise(resolve => setTimeout(resolve, 50));
    }
    
    console.log(`[Car] Preload complete: ${loaded}/${urlArray.length}`);
  }

  // ==================== STATE ====================

  selectAsset(category, assetId) {
    this.state.selections[category] = assetId;
    this.loadAsset(category);
  }

  // ==================== GRADIENT TONE ====================

  /**
   * Get the active gradient tone preset for a car category, or null if 'none'.
   */
  getActiveToneForCategory(category) {
    if (!CAR_GRADIENT_ELIGIBLE.has(category)) return null;
    const group = CAR_GRADIENT_TONE_GROUPS[category];
    if (!group) return null;
    const toneId = this.state.toneSelections[group];
    if (!toneId || toneId === 'none') return null;
    const preset = CAR_GRADIENT_PRESETS.find(p => p.id === toneId);
    return preset && preset.shadow ? preset : null;
  }

  /**
   * Set the gradient tone for a specific car part and reload it.
   */
  setPartTone(category, toneId) {
    const group = CAR_GRADIENT_TONE_GROUPS[category];
    if (!group) return;
    this.state.toneSelections[group] = toneId;
    this.loadAsset(category);
    this.renderControls();
  }

  // ==================== CANVAS ====================

  async loadAll() {
    const model = this.config.models.find(m => m.id === this.state.model);
    if (!model) return;
    await Promise.all(
      model.zIndexConfig.map(cat => this.loadAsset(cat))
    );
  }

  async loadAsset(category) {
    const layer = this.layers[category];
    if (!layer) return;

    this.loadCounters[category] = (this.loadCounters[category] || 0) + 1;
    const loadId = this.loadCounters[category];

    this.deallocateCanvases(layer);
    layer.destroyChildren();
    layer.batchDraw();

    const assetId = this.state.selections[category];
    const assets = this.config.assets[this.state.model]?.[category];
    const asset = assets?.find(a => a.id === assetId);

    if (!asset || !asset.src) {
      layer.batchDraw();
      return;
    }

    try {
      // Determine if gradient mapping is active for this category
      const tone = this.getActiveToneForCategory(category);
      let srcPath = asset.src;
      if (tone) {
        const grayPath = resolveGrayAssetPath(asset.src);
        if (grayPath) srcPath = grayPath;
      }

      const image = await this.loadImage(srcPath);
      if (this.loadCounters[category] !== loadId) return;

      // Apply gradient map with black-preservation threshold
      let imageSource = image;
      if (tone) {
        imageSource = GradientMap.apply(image, tone, { blackThreshold: CAR_BLACK_THRESHOLD });
      }

      const konvaImage = new Konva.Image({
        x: 0, y: 0,
        image: imageSource,
        width: this.config.canvas.width,
        height: this.config.canvas.height
      });

      layer.add(konvaImage);
      layer.batchDraw();
    } catch (err) {
      console.warn(`[Car] Failed: ${asset.src}`, err);
    }
  }

  loadImage(src) {
    // Request downsized images from Shopify CDN (2× canvas width for Retina).
    // Admin pages get full-resolution (resolveAssetUrl skips maxWidth on admin).
    const maxWidth = this.config.canvas.width * 2;
    const resolved = resolveAssetUrl(src, { maxWidth });
    return new Promise((resolve, reject) => {
      if (this.imageCache.has(resolved)) {
        resolve(this.imageCache.get(resolved));
        return;
      }
      const img = new Image();
      img.crossOrigin = 'Anonymous';
      img.onload = () => {
        // LRU Cache eviction check
        if (this.imageCache.size >= this.maxCacheSize) {
          const oldestKey = this.imageCache.keys().next().value;
          this.imageCache.delete(oldestKey);
        }
        this.imageCache.set(resolved, img);
        resolve(img);
      };
      img.onerror = reject;
      img.src = resolved;
    });
  }

  // ==================== RECIPE / EXPORT ====================

  generateRecipe() {
    return {
      model: this.state.model,
      selections: { ...this.state.selections },
      toneSelections: { ...this.state.toneSelections }
    };
  }

  setModel(modelId) {
    if (this.state.model === modelId) return;

    const oldModelId = this.state.model;
    this.state.model = modelId;

    // Preserve selections by ID match or index fallback
    Object.keys(this.state.selections).forEach(key => {
      if (key === 'model') return;
      const newAssets = this.config.assets[modelId]?.[key];
      if (!newAssets || newAssets.length === 0) return;

      const exactMatch = newAssets.find(a => a.id === this.state.selections[key]);
      if (exactMatch) return;

      const oldAssets = this.config.assets[oldModelId]?.[key] || [];
      const oldIndex = oldAssets.findIndex(a => a.id === this.state.selections[key]);
      if (oldIndex >= 0 && oldIndex < newAssets.length) {
        this.state.selections[key] = newAssets[oldIndex].id;
      } else {
        this.state.selections[key] = newAssets[0].id;
      }
    });

    this.recreateLayers();
    this.renderControls();
    this.loadAll();
  }

  toDataURL(options = {}) {
    return this.stage.toDataURL({ pixelRatio: options.pixelRatio || 1 });
  }

  // ==================== STANDALONE CONTROLS (Step 4 full-screen) ====================

  renderControls() {
    const panel = document.getElementById(this.controlsPanelId);
    if (!panel) return;

    panel.innerHTML = this.config.accordionSections.map((section, idx) => {
      const isOpen = this.openAccordion === section.id || (this.openAccordion === null && idx === 0);
      if (isOpen && this.openAccordion === null) this.openAccordion = section.id;

      return `
        <div class="accordion ${isOpen ? 'accordion--open' : ''}" data-car-accordion="${section.id}">
          <button class="accordion__header" data-car-acc-toggle="${section.id}">
            <span class="accordion__icon">${section.icon}</span>
            <span class="accordion__title">${section.label}</span>
            <span class="accordion__chevron">▼</span>
          </button>
          <div class="accordion__body">
            ${this.renderAccordionBody(section)}
          </div>
        </div>
      `;
    }).join('');

    this.bindControlEvents(panel);
  }

  renderAccordionBody(section) {
    if (section.type === 'model') {
      return this.renderModelGrid();
    }
    return this.renderSubSections(section);
  }

  renderModelGrid() {
    return `<div class="asset-grid">${
      this.config.models.map(model => `
        <div class="asset-item ${this.state.model === model.id ? 'asset-item--selected' : ''}"
             data-car-model="${model.id}">
          <div class="asset-item__preview">
            ${model.thumb
              ? `<img src="${resolveAssetUrl(model.thumb)}" alt="${model.name}" style="width:100%;height:100%;object-fit:contain;" />`
              : `<span style="font-size:24px;">🚗</span>`
            }
          </div>
          <span class="asset-item__name">${model.name}</span>
        </div>
      `).join('')
    }</div>`;
  }

  renderSubSections(section) {
    if (!section.subSections) return '';

    // Filter subtabs that have options for the current model
    const availableSubSections = section.subSections.filter(sub => {
      const items = this.config.assets[this.state.model]?.[sub.id];
      return items && items.length > 0;
    });

    if (availableSubSections.length === 0) {
      return '<div style="padding:10px;color:var(--dm-text-muted);font-size:0.8rem;">No parts available for this category.</div>';
    }

    // Determine active sub-tab for this section
    let activeSubId = this.activeSubSection[section.id];
    if (!activeSubId || !availableSubSections.find(s => s.id === activeSubId)) {
      activeSubId = availableSubSections[0].id;
      this.activeSubSection[section.id] = activeSubId;
    }

    const subTabs = availableSubSections.map(sub => `
      <button class="sub-tab ${activeSubId === sub.id ? 'sub-tab--active' : ''}"
              data-car-group="${section.id}" data-car-sub="${sub.id}">
        ${sub.label}
      </button>
    `).join('');

    const assets = this.config.assets[this.state.model]?.[activeSubId] || [];
    const grid = assets.map(asset => `
      <div class="asset-item ${this.state.selections[activeSubId] === asset.id ? 'asset-item--selected' : ''}"
           data-car-category="${activeSubId}" data-car-asset="${asset.id}">
        <div class="asset-item__preview">
          ${asset.src
            ? `<img src="${resolveAssetUrl(asset.src)}" alt="${asset.name}" loading="lazy" onerror="this.style.display='none'">`
            : '<span class="asset-item__none">✕</span>'}
        </div>
        <span class="asset-item__name">${asset.name}</span>
      </div>
    `).join('');

    // Add inline tone picker if this sub-section is gradient-eligible
    const tonePicker = CAR_GRADIENT_ELIGIBLE.has(activeSubId)
      ? this.renderInlineTonePicker(activeSubId)
      : '';

    return `
      <div class="sub-tabs">${subTabs}</div>
      <div class="asset-grid" data-car-grid-for="${activeSubId}">${grid}</div>
      ${tonePicker}
    `;
  }

  /**
   * Render an inline tone picker for a specific car part category.
   */
  renderInlineTonePicker(category) {
    const group = CAR_GRADIENT_TONE_GROUPS[category];
    if (!group) return '';
    const currentTone = this.state.toneSelections[group] || 'none';
    let label = category.replace(/_/g, ' ');
    label = label.charAt(0).toUpperCase() + label.slice(1);
    return `
      <div class="inline-tone-picker" style="margin-top:10px;padding-top:10px;border-top:1px solid var(--dm-border);">
        <div style="font-size:0.7rem;font-weight:600;color:var(--dm-text-muted);margin-bottom:6px;text-transform:uppercase;letter-spacing:0.5px;">
          🎨 ${label} Color
        </div>
        <div class="tone-grid tone-grid--inline">
          ${CAR_GRADIENT_PRESETS.map(t => {
            const isActive = t.id === currentTone ? ' tone-swatch--active' : '';
            const bg = t.shadow
              ? `linear-gradient(135deg, ${t.shadow}, ${t.darkMid || t.shadow}, ${t.mid}, ${t.lightMid || t.highlight}, ${t.highlight})`
              : 'conic-gradient(from 0deg, #f44, #f90, #ff0, #3c0, #09f, #c3f, #f44)';
            return `<button class="tone-swatch tone-swatch--sm${isActive}" data-car-tone-cat="${category}" data-car-tone-id="${t.id}" style="background:${bg}" title="${t.name}"><span class="tone-swatch__label">${t.name}</span></button>`;
          }).join('')}
        </div>
      </div>
    `;
  }

  bindControlEvents(container) {
    // Accordion toggles
    container.querySelectorAll('[data-car-acc-toggle]').forEach(btn => {
      btn.addEventListener('click', e => {
        const id = e.currentTarget.dataset.carAccToggle;
        const wasOpen = this.openAccordion === id;
        this.openAccordion = wasOpen ? null : id;

        container.querySelectorAll('.accordion').forEach(acc => {
          const accId = acc.dataset.carAccordion;
          if (accId === id && !wasOpen) {
            acc.classList.add('accordion--open');
          } else {
            acc.classList.remove('accordion--open');
          }
        });
      });
    });

    // Sub-tab clicks
    container.querySelectorAll('[data-car-sub]').forEach(btn => {
      btn.addEventListener('click', e => {
        const group = e.currentTarget.dataset.carGroup;
        const sub = e.currentTarget.dataset.carSub;
        this.activeSubSection[group] = sub;
        // Re-render controls to update sub-tab + grid
        this.renderControls();
      });
    });

    // Model clicks
    container.querySelectorAll('[data-car-model]').forEach(item => {
      item.addEventListener('click', e => {
        const modelId = e.currentTarget.dataset.carModel;
        this.setModel(modelId);
      });
    });

    // Asset clicks
    container.querySelectorAll('[data-car-category]').forEach(item => {
      item.addEventListener('click', e => {
        const cat = e.currentTarget.dataset.carCategory;
        const assetId = e.currentTarget.dataset.carAsset;
        this.selectAsset(cat, assetId);

        // Update UI selection for this category
        container.querySelectorAll(`[data-car-category="${cat}"]`).forEach(el =>
          el.classList.remove('asset-item--selected')
        );
        e.currentTarget.classList.add('asset-item--selected');
      });
    });

    // Tone swatch clicks (per-part)
    container.querySelectorAll('[data-car-tone-cat]').forEach(btn => {
      btn.addEventListener('click', e => {
        const cat = e.currentTarget.dataset.carToneCat;
        const toneId = e.currentTarget.dataset.carToneId;
        this.setPartTone(cat, toneId);
      });
    });
  }
}
